import {
    createRestClient,
    RestClient,
} from '@superbet-group/web.lib.rest';

import { defaultHttpTimeoutMs } from '@core/constants';
import {
    HotTournament,
    PopularSuperbet,
    PopularSuperbetV2,
    PopularSuperbetV2DTO,
} from '@store/modules/data/sportOffer/types';
import { getConfig } from '@superbet-group/web.lib.config';
import { transformOfferEvent } from '@core/mappers/offer/transformOfferEvent';
import { StructManager } from './StructManager';
import { OfferEventDto } from '@core/mappers/offer/dtos/OfferEventDto';
import { GetTopEventsOptions } from './types';
import { TopEventType } from './enums';

const restClient: RestClient = createRestClient(
    `${getConfig().cms?.baseUrl}/cached-superbet/`,
    defaultHttpTimeoutMs,
);

export function getHotTournaments(): Promise<HotTournament[]> {
    const date = new Date().toJSON().slice(0, 10);

    // At least for the time being, this is based on country, not user language, since
    // different targets must have different setups, not different languages.
    // Multilang targets will always serve English content (BE, COM)
    const country = getConfig().countrySpecific.country;
    return restClient.get<HotTournament[]>(`/hot-tournaments/offer/${country}/${date}`);
}

export function getPopularSuperbets(): Promise<PopularSuperbet[]> {
    const date = new Date().toJSON().slice(0, 10);
    const country = getConfig().countrySpecific.country;
    return restClient.get<PopularSuperbet[]>(`/popular-superbets/${country}/${date}`);
}

export async function getPopularSuperbetsV2(): Promise<PopularSuperbetV2[]> {
    const date = new Date().toJSON().slice(0, 10);
    const config = getConfig();
    const country = config.countrySpecific.country;
    const locale = config.appLocale.locale;
    const dtos = await restClient.get<PopularSuperbetV2DTO[]>(`/popular-superbets/v2/${country}/${date}?locale=${locale}`);
    return dtos.map((dto: PopularSuperbetV2DTO) => {
        const event = transformOfferEvent(dto);
        StructManager.getInstance().denormalizeOdds(event);
        return {
            event,
            popularSuperbetId: dto.popularSuperbetId,
        };
    });
}

function getTopEventsUrl(options: Partial<GetTopEventsOptions> = {}) {
    const { sportId, eventType = TopEventType.Offer } = options;
    const { country } = getConfig().countrySpecific;

    let url = `/top-events/${eventType}/${country}`;

    if (sportId) {
        url += `/${sportId}`;
    }

    const { locale } = getConfig().appLocale;
    const searchParams = new URLSearchParams({
        locale,
    });

    return { url, searchParams };
}

export function getTopEvents(options?: Partial<GetTopEventsOptions>): Promise<{ data: OfferEventDto[] }> {
    const { url, searchParams } = getTopEventsUrl(options);

    return restClient.get<{ data: OfferEventDto[] }>(`${url}?${searchParams.toString()}`);
}

export function getTopEventsPreview(options?: Partial<GetTopEventsOptions>): Promise<{ data: Pick<OfferEventDto, 'eventId' | 'categoryId' | 'tournamentId'>[] }> {
    const { url, searchParams } = getTopEventsUrl(options);

    searchParams.append('banner', true.toString());

    return restClient.get<{ data: Pick<OfferEventDto, 'eventId' | 'categoryId' | 'tournamentId'>[] }>(`${url}?${searchParams.toString()}`);
}
